// src/components/job-engine/JobsListPage.tsx
import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Stack, Text, TextField, DatePicker, Dropdown, IDropdownOption, PrimaryButton, DefaultButton,
  MessageBar, MessageBarType, DetailsList, SelectionMode, IColumn, IconButton, Icon,
} from "@fluentui/react";
import { PaginationFooter } from "@/ui-controls/PaginationFooter";
import ConnectionStatusBadge from "./ConnectionStatusBadge";
import { useJobs } from "./useJobs";
import { useJobTypesMeta } from "./useJobTypesMeta";
import useJobEngineEvent from "./useJobEngineEvent";
import { useJobEngineConnectionState } from "./useJobEngineConnectionState";
import StatePill from "./StatePill";
import { formatDateTime, formatDuration } from "./formatters";
import type { Job, JobState, JobsQuery } from "@/api/jobEngine";

const ALL_STATES: JobState[] = ["Enqueued", "Started", "Succeeded", "Failed", "DeadLettered"];
const stateOptions: IDropdownOption[] = ALL_STATES.map((s) => ({ key: s, text: s }));

const JobsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCorrelation = searchParams.get("correlationId") ?? "";
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(25);
  const [state, setState] = useState<JobState | "">("");
  const [jobType, setJobType] = useState<string>("");
  const [correlationId, setCorrelationId] = useState<string>(initialCorrelation);
  const [fromCreatedAtUtc, setFrom] = useState<string>("");
  const [toCreatedAtUtc, setTo] = useState<string>("");

  const query: JobsQuery = {
    state: state || null,
    jobType: jobType || null,
    correlationId: correlationId || null,
    fromCreatedAtUtc: fromCreatedAtUtc || null,
    toCreatedAtUtc: toCreatedAtUtc || null,
    pageNumber,
    pageSize,
  };

  const { items, totalCount, isLoading, error, refetch } = useJobs(query);
  const meta = useJobTypesMeta();

  useJobEngineEvent("JobStarted",      refetch);
  useJobEngineEvent("JobFinished",     refetch);
  useJobEngineEvent("JobFailed",       refetch);
  useJobEngineEvent("JobDeadLettered", refetch);

  const isConnected = useJobEngineConnectionState();
  React.useEffect(() => {
    if (isConnected) return;
    const id = window.setInterval(refetch, 10_000);
    return () => window.clearInterval(id);
  }, [isConnected, refetch]);

  const jobTypeOptions: IDropdownOption[] = useMemo(
    () => (meta.data?.jobTypes ?? []).map((t) => ({ key: t.jobType, text: t.jobType })),
    [meta.data],
  );

  const columns: IColumn[] = [
    { key: "parent", name: "", minWidth: 28, maxWidth: 28, onRender: (i: Job) => i.parentJobId
      ? <Icon iconName="OrgChart" title={`Triggered by ${i.parentJobId}`} styles={{ root: { color: "#0078d4", cursor: "help" } }} />
      : null },
    { key: "state", name: "State", minWidth: 70, maxWidth: 90, onRender: (item: Job) => <StatePill state={item.state} /> },
    { key: "jobType", name: "Type", fieldName: "jobType", minWidth: 240 },
    { key: "createdAt", name: "Created", minWidth: 160, onRender: (i: Job) => formatDateTime(i.createdAt) },
    { key: "startedAt", name: "Started", minWidth: 160, onRender: (i: Job) => formatDateTime(i.startedAt) },
    { key: "finishedAt", name: "Finished", minWidth: 160, onRender: (i: Job) => formatDateTime(i.finishedAt) },
    { key: "duration", name: "Duration", minWidth: 80, onRender: (i: Job) => formatDuration(i.startedAt, i.finishedAt) },
    { key: "attemptCount", name: "Attempts", fieldName: "attemptCount", minWidth: 80 },
    { key: "correlationId", name: "Correlation", minWidth: 180, onRender: (i: Job) => (
      <Text variant="small">{i.correlationId}</Text>
    ) },
    { key: "link", name: "", minWidth: 40, maxWidth: 40, onRender: (i: Job) => (
      <IconButton
        iconProps={{ iconName: "OpenInNewWindow" }}
        title="Open job detail"
        ariaLabel="Open job detail"
        onClick={() => navigate(`/job-engine/jobs/${encodeURIComponent(i.jobId)}`)}
      />
    ) },
  ];

  const reset = () => {
    setState(""); setJobType(""); setCorrelationId(""); setFrom(""); setTo(""); setPageNumber(1);
  };

  return (
    <div style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
      <div className="page-header">
        <h2 className="page-title">Jobs</h2>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <ConnectionStatusBadge isConnected={isConnected} />
        </Stack>
      </div>
      <Stack tokens={{ childrenGap: 12 }}>

      {/* Native flex+gap instead of Stack childrenGap: TextField/DatePicker
          bake margin:0 into their root class which wins specificity over
          Stack's generated child-margin rule, breaking alignment. CSS gap
          on the container can't be overridden by child margin. */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 16 }}>
        <Dropdown
          label="State"
          options={[{ key: "", text: "Any" }, ...stateOptions]}
          selectedKey={state}
          onChange={(_, o) => setState(o?.key as JobState | "")}
          styles={{ root: { minWidth: 160 }, label: { whiteSpace: "nowrap" } }}
        />
        <Dropdown
          label="Type"
          options={[{ key: "", text: "Any" }, ...jobTypeOptions]}
          selectedKey={jobType}
          onChange={(_, o) => setJobType(String(o?.key ?? ""))}
          styles={{ root: { minWidth: 200 }, label: { whiteSpace: "nowrap" } }}
        />
        <TextField
          label="Correlation"
          value={correlationId}
          onChange={(_, v) => setCorrelationId(v ?? "")}
          styles={{
            root: {
              minWidth: 200,
              // Same description-wrapper collapse as DatePicker — TextField always
              // renders <span class="ms-TextField-description"> for the aria-live
              // status region, which reserves vertical space even when empty.
              selectors: {
                ".ms-TextField-description": { display: "none" },
                '[id^="TextFieldDescription"]': { display: "none" },
              },
            },
            subComponentStyles: { label: { root: { whiteSpace: "nowrap" } } },
          }}
        />
        <DatePicker
          label="From"
          value={fromCreatedAtUtc ? new Date(fromCreatedAtUtc) : undefined}
          onSelectDate={(d) => setFrom(d ? d.toISOString() : "")}
          styles={{
            root: {
              minWidth: 180,
              // Fluent renders the statusMessage inside a <span class="ms-TextField-description">
              // wrapper that reserves vertical space even when the inner status div is empty.
              // statusMessage: display:none only collapses the inner div; we have to hide the
              // outer span too or the DatePicker root's bottom sits below the input, which
              // pushes the bottom-aligned Search/Reset buttons below the visible input row.
              selectors: {
                ".ms-TextField-description": { display: "none" },
                '[id^="TextFieldDescription"]': { display: "none" },
              },
            },
            statusMessage: { display: "none" },
          }}
          placeholder="Select date..."
          allowTextInput
        />
        <DatePicker
          label="To"
          value={toCreatedAtUtc ? new Date(toCreatedAtUtc) : undefined}
          onSelectDate={(d) => setTo(d ? d.toISOString() : "")}
          styles={{
            root: {
              minWidth: 180,
              // Fluent renders the statusMessage inside a <span class="ms-TextField-description">
              // wrapper that reserves vertical space even when the inner status div is empty.
              // statusMessage: display:none only collapses the inner div; we have to hide the
              // outer span too or the DatePicker root's bottom sits below the input, which
              // pushes the bottom-aligned Search/Reset buttons below the visible input row.
              selectors: {
                ".ms-TextField-description": { display: "none" },
                '[id^="TextFieldDescription"]': { display: "none" },
              },
            },
            statusMessage: { display: "none" },
          }}
          placeholder="Select date..."
          allowTextInput
        />
        <PrimaryButton text="Search" onClick={refetch} />
        <DefaultButton text="Reset" onClick={reset} />
      </div>

      {!isConnected && (
        <MessageBar messageBarType={MessageBarType.warning}>
          Live updates unavailable — polling.
        </MessageBar>
      )}
      {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}

      <DetailsList
        items={items}
        columns={columns}
        selectionMode={SelectionMode.none}
        compact={true}
      />

      <PaginationFooter
        currentPage={pageNumber}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={(p) => setPageNumber(p)}
      />
    </Stack>
    </div>
  );
};

export default JobsListPage;
